import { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { checkPharmacyStatus, getBusinessHours } from '../../utils/businessHours';

export default function Banner() {
  const { t } = useLanguage();
  const [pharmacyStatus, setPharmacyStatus] = useState(() => checkPharmacyStatus(t));
  const statusRef = useRef(null);

  useEffect(() => {
    // Update status immediately
    const currentStatus = checkPharmacyStatus(t);
    setPharmacyStatus(currentStatus);

    // Set up interval to check status every minute
    // Removed requestAnimationFrame as it's unnecessary for 60s intervals
    const interval = setInterval(() => {
      const newStatus = checkPharmacyStatus(t);
      setPharmacyStatus(newStatus);
      
      // Announce status changes to screen readers
      if (statusRef.current) {
        const statusText = newStatus.isOpen ? t('banner.openStatus') : t('banner.closedStatus');
        statusRef.current.textContent = statusText;
      }
    }, 60000); // Check every minute

    // Cleanup interval on component unmount
    return () => clearInterval(interval);
  }, [t]);

  return (
    <nav
      // A shade deeper than the Nav's brand red: the utility strip reads as
      // its own quiet layer above the main bar instead of merging into it.
      className="relative z-[2] overflow-hidden bg-banner"
      aria-label={t('banner.contactInfo')}
    >
      {/* Screen reader only live region for status changes */}
      <div className="sr-only" aria-live="polite" aria-atomic="true" ref={statusRef}></div>

      <div className="relative max-w-9xl mx-auto">
        <div className="flex items-center justify-between px-4 py-2 sm:px-6 lg:px-10">
          <div className="flex items-center justify-between w-full gap-3 sm:gap-6 md:gap-8 lg:gap-10 xl:gap-12 2xl:gap-16 flex-nowrap banner-responsive overflow-x-auto scrollbar-hide">
            <a
              href="https://www.google.com/maps/place/Dreirosen+Apotheke/@47.5688986,7.5906421,17z/data=!4m6!3m5!1s0x4791b9a3ba68615b:0xed2e0461c6758e53!8m2!3d47.5688988!4d7.5906717!16s%2Fg%2F1tlvnfwl?entry=ttu&g_ep=EgoyMDI0MTEwNi4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center text-white/80 hover:text-white transition-colors duration-200 whitespace-nowrap flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-banner rounded-full"
              aria-label={t('banner.ariaLabels.openLocation')}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15 ring-1 ring-inset ring-white/15 flex-shrink-0 transition-colors duration-200 group-hover:bg-white/25" aria-hidden="true">
                <svg
                  className="h-3.5 w-3.5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 5.2 11.25 6.6 12.88a1.25 1.25 0 001.8 0C13.8 20.25 19 14.25 19 9c0-3.87-3.13-7-7-7zm0 9.25a2.25 2.25 0 110-4.5 2.25 2.25 0 010 4.5z" />
                </svg>
              </div>
              <span className="ml-2 text-[13px] font-medium">{t('banner.pharmacyName')}</span>
            </a>

            <a
              href="tel:+41616917488"
              className="group flex items-center text-white/80 hover:text-white transition-colors duration-200 whitespace-nowrap flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-banner rounded-full"
              aria-label={t('banner.ariaLabels.callPharmacy')}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15 ring-1 ring-inset ring-white/15 flex-shrink-0 transition-colors duration-200 group-hover:bg-white/25" aria-hidden="true">
                <svg
                  className="h-3.5 w-3.5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M6.62 10.79a15.564 15.564 0 006.59 6.59l2.2-2.2a1.002 1.002 0 011.11-.27 11.493 11.493 0 003.59.61c.55 0 1 .45 1 1V20a1 1 0 01-1 1c-9.39 0-17-7.61-17-17a1 1 0 011-1h3.5c.55 0 1 .45 1 1 0 1.25.21 2.47.61 3.59.14.34.05.74-.27 1.11l-2.2 2.2z" />
                </svg>
              </div>
              <span className="ml-2 text-[13px] font-medium">+41 61 691 74 88</span>
            </a>

            <a
              href="mailto:info@dreirosenapotheke.ch"
              className="group flex items-center text-white/80 hover:text-white transition-colors duration-200 whitespace-nowrap flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-banner rounded-full"
              aria-label={t('banner.ariaLabels.emailPharmacy')}
            >
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15 ring-1 ring-inset ring-white/15 flex-shrink-0 transition-colors duration-200 group-hover:bg-white/25" aria-hidden="true">
                <svg
                  className="h-3.5 w-3.5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                </svg>
              </div>
              <span className="ml-2 text-[13px] font-medium hidden sm:inline">info@dreirosenapotheke.ch</span>
              <span className="ml-2 text-[13px] font-medium sm:hidden">{t('banner.email')}</span>
            </a>

            <div className="group flex items-center text-white/80 whitespace-nowrap flex-shrink-0" aria-label={`${t('banner.openingHours')} ${getBusinessHours(t)}`}>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15 ring-1 ring-inset ring-white/15 flex-shrink-0 transition-colors duration-200 group-hover:bg-white/25" aria-hidden="true">
                <svg
                  className="h-3.5 w-3.5 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z" />
                </svg>
              </div>
              <span className="ml-2 text-[13px] font-medium hidden lg:inline">{getBusinessHours(t)}</span>
              <span className="ml-2 text-[13px] font-medium lg:hidden">{t('banner.businessHoursShort')}</span>
            </div>

            <div
              role="status"
              aria-live="polite"
              aria-atomic="true"
              className="group flex items-center text-white/80 whitespace-nowrap flex-shrink-0"
              aria-label={`${t('banner.ariaLabels.pharmacyStatus')} ${pharmacyStatus.status}`}
            >
              <span className="sr-only">{t('banner.status')} </span>
              <div className="flex items-center justify-center w-6 h-6 rounded-full bg-white/15 ring-1 ring-inset ring-white/15 flex-shrink-0 transition-colors duration-200 group-hover:bg-white/25" aria-hidden="true">
                <span className="relative flex h-2 w-2">
                  {pharmacyStatus.isOpen && (
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-300 opacity-75" />
                  )}
                  <span
                    className={`relative inline-flex h-2 w-2 rounded-full ${
                      pharmacyStatus.isOpen ? 'bg-emerald-400 ring-1 ring-white/70' : 'bg-white/60'
                    }`}
                  />
                </span>
              </div>
              <span className="ml-2 text-[13px] font-medium hidden sm:inline">{pharmacyStatus.status}</span>
              <span className="ml-2 text-[13px] font-medium sm:hidden">{pharmacyStatus.isOpen ? t('banner.open') : t('banner.closed')}</span>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
}
